import api from 'api'
import { Cell, Grid, HFlow, Modal, ModalBody, ModalFooter, ModalFooterButton, ModalHeader, Text } from 'bold-ui'
import { useAutorizacaoRetiradaAbsorventesQuery, useCidadaoQuery } from 'graphql/hooks.generated'
import { useAnalytics } from 'hooks/analytics/useAnalytics'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import React, { useState } from 'react'
import { Redirect } from 'react-router-dom'
import { isUndefinedOrNull } from 'util/checks'

import { DignidadeMenstrualStatusAutorizacaoAbsorventeEnum } from './model-dignidadeMenstrual'
import { StatusAutorizacaoAbsorventes } from './StatusAutorizacaoAbsorventes'

interface AutorizacaoAbsorventesModalProps {
  open: boolean
  cidadaoId: ID
  onClose: () => void
}

export function AutorizacaoAbsorventesModal(props: Readonly<AutorizacaoAbsorventesModalProps>) {
  const { analytics } = useAnalytics()

  const { open, cidadaoId, onClose: handleClose } = props

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao()

  const [isRunning, setIsRunning] = useState<boolean>(false)

  const { data: dataCidadao, loading: loadingCidadao } = useCidadaoQuery({
    variables: { id: cidadaoId },
  })

  const cidadaoPossuiCpf = !isUndefinedOrNull(dataCidadao.cidadao?.cpf)

  const { data: dataAutorizacao, loading: loadingAutorizacao } = useAutorizacaoRetiradaAbsorventesQuery({
    variables: { cidadaoId },
    skip: !cidadaoPossuiCpf,
    context: { newRequest: true },
  })

  const handleGerar = () => {
    analytics.logEvent('gerar_doc_absorventes')
    setIsRunning(true)
    const autorizacao = dataAutorizacao?.autorizacaoRetiradaAbsorventeByCidadaoId?.autorizacao
    const cidadaoAutorizacao = dataAutorizacao?.autorizacaoRetiradaAbsorventeByCidadaoId?.autorizacao?.cidadao
    return handleImpressaoPath({
      apiPath: api.dignidadeMenstrual.gerarAutorizacaoRetiradaAbsorventes,
      values: {
        cidadaoId: cidadaoId,
        cidadaoDignidadeMenstrualInput: {
          cns: cidadaoAutorizacao?.cns,
          dataNascimento: cidadaoAutorizacao?.dataNascimento,
          nome: cidadaoAutorizacao?.nome,
          nomeMae: cidadaoAutorizacao?.nomeMae,
        },
        rmsDignidadeMenstrualInput: {
          codigo: autorizacao?.codigoRms,
          uf: autorizacao?.ufRms,
        },
        hashDocumento: autorizacao?.hash,
      },
      file: 'autorizacao_absorventes.pdf',
      dateFormat: reportFormatDate,
    })
      .catch((error) => handleImpressaoError(error, error.message ?? 'Erro inesperado! Tente novamente mais tarde.'))
      .finally(() => {
        setIsRunning(false)
        handleClose()
      })
  }

  const statusAutorizacao = dataAutorizacao.autorizacaoRetiradaAbsorventeByCidadaoId?.status
  const isLoading = loadingCidadao || loadingAutorizacao
  const isGerarBtnDisabled =
    isLoading ||
    statusAutorizacao !== DignidadeMenstrualStatusAutorizacaoAbsorventeEnum.OK ||
    isRunning ||
    !cidadaoPossuiCpf

  if (!isLoading && isUndefinedOrNull(dataCidadao.cidadao)) {
    return <Redirect to='/404' />
  }

  return (
    <Modal size='small' open={open} onClose={handleClose}>
      <ModalHeader title='Documento de autorização para retirada de absorventes' />
      <ModalBody>
        <Grid>
          <Cell size={12}>
            <Text>
              A oferta gratuita de absorventes higiênicos através do Programa de Dignidade Menstrual se dá através de
              alguns critérios. Para gerar o documento que autoriza a retirada dos absorventes, o cidadão precisa
              atender aos critérios do programa.
            </Text>
          </Cell>
          <Cell size={12}>
            <StatusAutorizacaoAbsorventes
              loading={isLoading}
              cidadaoPossuiCpf={cidadaoPossuiCpf}
              statusAutorizacao={statusAutorizacao}
            />
          </Cell>
        </Grid>
      </ModalBody>
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <ModalFooterButton onClick={handleClose}>Cancelar</ModalFooterButton>
          <ModalFooterButton loading={isRunning} kind='primary' onClick={handleGerar} disabled={isGerarBtnDisabled}>
            Gerar documento
          </ModalFooterButton>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}
